import { Divider, Stack } from '@mui/material';
import { compact, find, isEmpty, map, sortBy } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DescriptionRow, CopyText } from 'components';
import { RequisitesCascadeMode } from 'enums';
import { useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Cascade } from 'types';

type Props = {
  cascade: Cascade | null;
};

export const CascadeDetails: React.FC<Props> = ({ cascade }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.cascades.cascade_details',
  });
  const { t: tList } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.cascades.cascades_list.table',
  });

  const { p2pProviders } = useUserContext();

  const getCascadeModeLabel = useCallback(
    (mode: RequisitesCascadeMode) => {
      switch (mode) {
        case RequisitesCascadeMode.TradersFirst:
          return tList('modes.traders_first');
        case RequisitesCascadeMode.ProvidersFirst:
          return tList('modes.providers_first');
        case RequisitesCascadeMode.TradersOnly:
          return tList('modes.traders_only');
        case RequisitesCascadeMode.ProvidersOnly:
          return tList('modes.providers_only');
        case RequisitesCascadeMode.Parallel:
          return tList('modes.parallel');
        default:
          return mode;
      }
    },
    [tList],
  );

  const getCascadeModeLabelPayout = useCallback(
    (mode: RequisitesCascadeMode) => {
      switch (mode) {
        case RequisitesCascadeMode.TradersFirst:
          return tList('modes.default');
        default:
          return getCascadeModeLabel(mode);
      }
    },
    [tList, getCascadeModeLabel],
  );

  const cascadeP2PProviders = useMemo(
    () =>
      compact(
        map(sortBy(cascade?.p2pProvidersConfigs || [], 'priority'), (config) =>
          find(p2pProviders, ({ id }) => id === config.p2pProviderId),
        ),
      ),
    [cascade?.p2pProvidersConfigs, p2pProviders],
  );

  if (!cascade) {
    return null;
  }

  return (
    <Stack direction="column" spacing={6}>
      <Stack direction="column" spacing={3}>
        <DescriptionRow title={t('rows.id')} value={cascade.id} canCopy />
        <DescriptionRow title={t('rows.name')} value={cascade.name} />
        <DescriptionRow
          title={t('rows.payin_mode')}
          value={getCascadeModeLabel(cascade.payinCascadeMode)}
        />
        <DescriptionRow
          title={t('rows.payout_mode')}
          value={getCascadeModeLabelPayout(cascade.payoutCascadeMode)}
        />
      </Stack>

      {!isEmpty(cascadeP2PProviders) && (
        <Stack direction="column" spacing={3}>
          <Divider textAlign="left">{t('titles.p2p_providers')}</Divider>

          {map(cascadeP2PProviders, (provider) => (
            <Stack direction="column" spacing={0} key={provider.id}>
              <CopyText text={provider.id} />
              <div>{provider.name}</div>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
